import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

// Grid의 roving focus를 공유하며 셀 안에 별도 Tab 정지점을 만들지 않습니다.
export const TableCellFocusContext = createContext<boolean | undefined>(undefined);
export const TableGridFocusContext = createContext(true);

export function TableCellTooltip({ children, fullText }: { children: ReactNode; fullText?: string }) {
  const gridFocused = useContext(TableCellFocusContext);
  const gridActive = useContext(TableGridFocusContext);
  const contentRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [pointerOpen, setPointerOpen] = useState(false);
  const [localFocused, setLocalFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const focused = gridFocused === undefined ? localFocused : gridFocused && gridActive;

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !fullText) return;
    const elements = [...content.querySelectorAll<HTMLElement>('[data-table-text]')];
    const measure = () => setTruncated(elements.some((element) => (
      element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
    )));
    measure();
    const observer = new ResizeObserver(measure);
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [fullText, children]);

  useEffect(() => {
    if (!focused) setDismissed(false);
  }, [focused]);

  return <Tooltip
    title={truncated ? fullText ?? '' : ''}
    describeChild
    open={Boolean(fullText) && truncated && !dismissed && (focused || pointerOpen)}
    onOpen={() => { setPointerOpen(true); setDismissed(false); }}
    onClose={() => { setPointerOpen(false); setDismissed(true); }}
    slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
  >
    <Box
      ref={contentRef}
      tabIndex={gridFocused === undefined && truncated ? 0 : undefined}
      onFocus={() => setLocalFocused(true)}
      onBlur={() => setLocalFocused(false)}
      sx={{ minWidth: 0, width: '100%' }}
    >{children}</Box>
  </Tooltip>;
}

export type TableTextCellProps = {
  children: ReactNode;
  fullText?: string;
  lines?: 1 | 2;
  variant?: 'body2' | 'caption';
  color?: 'text.primary' | 'text.secondary';
  align?: 'left' | 'center' | 'right';
};

export function TableTextCell({ children, fullText, lines = 1, variant = 'body2', color, align = 'left' }: TableTextCellProps) {
  const text = fullText ?? (typeof children === 'string' || typeof children === 'number' ? String(children) : undefined);
  return <TableCellTooltip fullText={text}>
    <Typography
      component="div"
      data-table-text
      variant={variant}
      color={color}
      sx={{
        minWidth: 0,
        textAlign: align,
        overflow: 'hidden',
        ...(lines === 1
          ? { whiteSpace: 'nowrap', textOverflow: 'ellipsis' }
          : { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflowWrap: 'anywhere', whiteSpace: 'normal' }),
      }}
    >{children}</Typography>
  </TableCellTooltip>;
}
